// screens/ProjectScreen.tsx — H3a (COCKPIT-HOME-COLUMNS-SPEC.md §H3): one
// screen component driven by the column's own `output` list
// (`CockpitProjectOutput[]`) plus, for the two projects whose table row
// names it (Rexx, VoiceStrip), the SAME `projects/<slug>` status fact the
// board's top card reads (`findProjectFact`, lib/cockpitBoard.ts) — never
// a second read of a different shape.
//
// Per the brief's table (and two reviewer corrections, 2026-10-16):
//   Rexx          — NEVER framed (Huan's decision `0bfce58a`): the status
//                    fact + the Record window scoped to Rexx (shots/docs)
//                    + two link buttons (test site, the live product).
//   Studio Stays  — the PUBLIC front page framed (signed-out; `/office`
//                    never rides a frame) + "Open the office" (new tab).
//   VoiceStrip    — the status fact's build row on top (+ "Install" when
//                    the fact carries a link) + the site framed below.
//   Origin Care   — the Record window scoped to Origin Care + "Open the
//                    briefing room" (new tab). Never framed either (the
//                    briefing room's own CSP, `frame-ancestors 'none'`).
//
// No instructional captions anywhere on this screen (voice canon §2.5) —
// the parts simply appear; "Reported as a judgement call" lives in the
// build report, never on screen.
import { useEffect, useRef, useState } from 'preact/hooks';
import { ApiError, getCockpitColumns, getSystemsSummary, type CockpitColumn, type ProjectSlug, type TodaySystemFact } from '../lib/api';
import { findProjectFact } from '../lib/cockpitBoard';
import { ProjectFrame } from '../components/ProjectFrame';
import { readCockpitPlace, writeCockpitPlace } from '../lib/cockpitPlace';
import { RecordHome } from './RecordHome';
import './ProjectScreen.css';

export interface ProjectScreenProps {
  tenantId: string;
  slug: ProjectSlug;
  onNavigate: (path: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; column: CockpitColumn | null; fact: TodaySystemFact | undefined };

/** Rexx and VoiceStrip show the status fact's own row at the top of the
 * screen (the table's rows (a) and the "build row" respectively); Studio
 * Stays and Origin Care rely on the board's own card instead — the
 * table's row for each names only what THAT screen shows. Studio Assist
 * (coordinator scope addition, 2026-10-16) shows it too — it is the ONLY
 * thing this screen has to show until something is deployed. */
const SHOW_STATUS_FACT = new Set<ProjectSlug>(['rexx', 'voicestrip', 'studio']);

// THE PROJECTS CONTRACT (coordinator, round 2, 2026-10-16): the reporter
// posts state 'up' or 'down' only — never 'needs_you'/'working', neither
// of which Core's own SYSTEM_FACT_STATE enum admits (packages/core/src/
// systems/types.ts). 'stale' was never a real state either — it is the
// COMPUTED `f.stale` boolean (systems/types.ts's own `computeStale`), so
// it is checked as a field here, never matched as a `state` value the
// way this switch used to (wrongly) try to.
function stateWord(f: TodaySystemFact): string {
  if (f.stale) return 'Stale';
  if (f.state === 'down') return 'Needs you';
  // Studio Assist (coordinator scope addition, 2026-10-16): the one
  // project whose reporter posts 'pending' — nothing deployed yet, never
  // needsYou (cockpitBoard.ts's own cardFromProjectFact), but still its
  // own word rather than the misleading 'OK'.
  if (f.state === 'pending') return 'Pending';
  return 'OK';
}

function FactRow({ fact, showInstall }: { fact: TodaySystemFact; showInstall: boolean }) {
  return (
    <div class="pscreen__fact" data-testid="project-fact-row">
      <span class="pscreen__fact-state">{stateWord(fact)}</span>
      <span class="pscreen__fact-detail">{fact.detail ?? fact.label}</span>
      {showInstall && fact.link ? (
        <a class="pscreen__fact-install" href={fact.link} target="_blank" rel="noopener noreferrer">
          Install
        </a>
      ) : null}
    </div>
  );
}

function OutputLink({ url, label }: { url: string; label: string }) {
  return (
    <a class="pscreen__linkbtn" href={url} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );
}

interface ProjectPlace {
  scrollTop: number;
}

export function ProjectScreen({ tenantId, slug, onNavigate }: ProjectScreenProps) {
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  // H3 build item 5 — "each project screen keeps its scroll under
  // `cockpit.place.project:<slug>` (H2's helper)." Same shape as Mail.tsx's
  // own `listScrollRef`/`placeScrollApplied`: the offset restores ONCE the
  // real content exists to scroll to, never before, and only the FIRST
  // time per mount.
  const scrollRef = useRef<HTMLDivElement>(null);
  const placeScrollApplied = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setState({ phase: 'loading' });
    placeScrollApplied.current = false;
    // H3b — the SAME systems-summary read the board makes (migration 0936
    // widens `system_facts.section` to admit 'projects'); never Today's
    // `meters.facts`/`proceeding.agents`, which H3a's own version of
    // `findProjectFact` read before that section existed.
    Promise.all([getCockpitColumns(tenantId), getSystemsSummary(tenantId)])
      .then(([{ columns }, summary]) => {
        if (cancelled) return;
        const column = columns.find((c) => c.id === `project:${slug}`) ?? null;
        const fact = findProjectFact(summary, slug);
        setState({ phase: 'ready', column, fact });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({ phase: 'error', message: err instanceof ApiError ? err.message : 'this project could not be read' });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, slug]);

  useEffect(() => {
    if (placeScrollApplied.current || state.phase !== 'ready' || !scrollRef.current) return;
    placeScrollApplied.current = true;
    const place = readCockpitPlace<ProjectPlace>(`project:${slug}`);
    if (place && typeof place.scrollTop === 'number') scrollRef.current.scrollTop = place.scrollTop;
  }, [state, slug]);

  function keepPlace() {
    if (!placeScrollApplied.current) return;
    writeCockpitPlace(`project:${slug}`, { scrollTop: scrollRef.current?.scrollTop ?? 0 });
  }

  if (state.phase === 'loading') {
    return (
      <div class="pscreen" data-testid={`project-${slug}`}>
        <div class="pscreen__skeleton" aria-hidden="true" />
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div class="pscreen" data-testid={`project-${slug}`}>
        <div class="pscreen__say">
          This project could not be read.
          <br />
          <span class="pscreen__say-soft">{state.message}</span>
        </div>
      </div>
    );
  }

  const { column, fact } = state;

  if (!column) {
    return (
      <div class="pscreen" data-testid={`project-${slug}`}>
        <div class="pscreen__say">
          This project isn't set up yet.
          <br />
          <button type="button" class="pscreen__home-link" onClick={() => onNavigate('/home')}>
            Home
          </button>
        </div>
      </div>
    );
  }

  const parts = column.output ?? [];

  return (
    <div class="pscreen" data-testid={`project-${slug}`} ref={scrollRef} onScroll={keepPlace}>
      <div class="pscreen__eyebrow">{column.title}</div>
      {SHOW_STATUS_FACT.has(slug) && fact ? <FactRow fact={fact} showInstall={slug === 'voicestrip'} /> : null}
      {/* Studio Assist (coordinator scope addition, 2026-10-16): nothing is
          deployed yet — the status fact (or its absence) plus this one
          quiet line is the WHOLE screen; no frame, no record window, no
          link button (column.output is empty for this project). */}
      {slug === 'studio' ? <div class="pscreen__say-soft">Not live yet.</div> : null}
      <div class="pscreen__parts">
        {parts.map((part, i) => {
          switch (part.kind) {
            case 'frame':
              return <ProjectFrame key={`frame-${i}`} url={part.url} title={column.title} />;
            case 'record':
              return (
                <RecordHome
                  key={`record-${i}`}
                  tenantId={tenantId}
                  initialQuery={part.query}
                  onOpenPage={(pageSlug) => onNavigate(`/record/page/${pageSlug}`)}
                  onOpenFile={(id) => onNavigate(`/record/file/${id}`)}
                  onOpenBrowse={(axis) => onNavigate(`/record/browse/${axis}`)}
                  onOpenReading={() => onNavigate('/record/reading')}
                />
              );
            case 'shots':
              // None of the four projects uses this kind this chunk (the
              // brief's own table); kept so a later chunk's output can add
              // one without a new screen branch.
              return (
                <div key={`shots-${i}`} class="pscreen__say">
                  No screenshots yet.
                </div>
              );
            case 'link':
              return <OutputLink key={`link-${i}`} url={part.url} label={part.label} />;
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
}
